import { Component } from 'react';
import { createRoot } from 'react-dom/client';

import { BrowserUtils } from '../../../ui-utils/browser-utils';
import style from './toast.component.module.scss';

interface ToastProps {
    /**
     * OPTIONAL since 2026-09-05 (lane N).
     *
     * Every existing caller passes a heading and a body ("Image Modified" / "We made your image
     * bigger…"), and that stays exactly as it was. The PDF editor's messages are single sentences
     * from ONE copy table (`components/pdf-editor/views/notices.copy.ts`) with no heading to give
     * them — inventing a category word here ("Font", "Not applied") would be a second copy source
     * and, per the editor's STATUS §3.4 rule 4, a word on screen that is for us rather than for
     * the reader. A toast with no title renders its sentence alone and skips the heading row.
     */
    title?: string;
    type?: 'primary' | 'success' | 'danger' | 'warning' | 'info';
    message: string;
    timeout?: number;
    canClose?: boolean;
    mustClose?: boolean;
    /**
     * The ARIA role of the toast box (lane N, 2026-09-05).
     *
     * A toast has never carried one, so nothing it said reached a screen reader. The PDF editor's
     * messages used to render as `role="status"` / `role="alert"` rows in the document column and
     * that role WAS their announcement — announced on insertion, which is what a message needs —
     * so moving them here without it would have made every substitution, refusal and page verdict
     * silent. Optional, and unset for every existing caller, so no other toast starts announcing.
     */
    role?: 'status' | 'alert';
    /** The copy row this toast renders, as `data-copy-key` — the handle QA rows address it by. */
    copyKey?: string;
    /** Accessible name for the close control. Unset leaves the control unnamed, as before. */
    closeLabel?: string;
    onOpen?: (modalRef: Toast) => void;
    onClose?: (modalRef: Toast) => void;
}

interface ToastState {
    shown: boolean;
}

export default class Toast extends Component<ToastProps, ToastState> {

    static showToast(request: ToastProps) {
        request.timeout = request.timeout || 3000;
        request.canClose = Object.keys(request).includes('canClose') ? request.canClose : true;
        this.createToast(<Toast {...request}>
        </Toast>, request);
    }

    static createToast(Cmp: any, props: ToastProps) {
        if (!BrowserUtils.hasDocument()) {
            console.error("Requested execution aborted since document was not avalilable");
            return;
        }
        const elementParent = document.createElement('div');
        // Removed bg- classes to rely on component styles
        elementParent.classList.add(`${Date.now()}`);
        let toasts = document.querySelector('#toasts');
        if (!toasts) {
            toasts = document.createElement('div');
            toasts.id = 'toasts';
            document.body.appendChild(toasts);
        }
        if (toasts) {
            toasts.appendChild(elementParent);
            const root = createRoot(elementParent!);
            root.render(Cmp);
        } else {
            console.error('Toasts not found');
        }
    }

    readonly state: Readonly<ToastState> = {
        shown: true,
    };
    constructor(props: ToastProps) {
        super(props);
        if (!props.mustClose && props.canClose) {
            setTimeout(() => {
                this.unmount();
            }, props.timeout);
        }
    }
    parentElement;
    setParentElement(element) {
        this.parentElement = element && element.parentElement;
    }

    componentDidMount() {
        if (this.props.onOpen) {
            this.props.onOpen(this);
        }
    }

    public unmount() {
        this.setState({
            shown: false,
        });
        if (this.props.onClose) {
            this.props.onClose(this);
        }
        try {
            if (this.parentElement) {
                this.parentElement.remove();
            }
        } catch (e) {
            console.error('Error while removing modal');
        }
    }

    render() {
        return (
            <div
                ref={(r) => this.setParentElement(r)}
                className={style.toast}
                data-type={this.props.type || 'primary'}
                data-copy-key={this.props.copyKey}
                role={this.props.role}
            >
                <div className={style.content}>
                    {/* The heading row is what the close control is positioned against, so it
                        renders whenever there is EITHER a title or a close button — a titleless
                        toast keeps its ×, it just has no words above the sentence. */}
                    {(this.props.title || this.props.canClose) &&
                        <div className={style.head}>
                            <div className={style['header-content']}>{this.props.title}</div>
                            {
                                this.props.canClose &&
                                // A BUTTON, not a div (lane N, 2026-09-05): the only way to
                                // dismiss a toast was a mouse press on an unnamed div, so a
                                // keyboard user could not reach it and a screen reader could not
                                // name it. The class and the geometry are untouched.
                                <button type="button" className={style.close}
                                    aria-label={this.props.closeLabel}
                                    style={{ border: 0, padding: 0, background: 'transparent' }}
                                    onClick={() => this.unmount()}>
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M9 1L1 9M1 1L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            }
                        </div>
                    }
                    <div className={style.body}>{this.props.message}</div>
                </div>
            </div>
        );
    }
}